import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGoogleMap } from '@react-google-maps/api';
import { MAP_PLACES_PRESETS, isPresetAvailable } from '../utils/mapPlacesPresets';

// Mirrors Google's own map type control (Map / Satellite), which cannot be
// extended, so "Places" sits beside it and looks like part of it.
const palette = (dark) => ({
  bg: dark ? '#202124' : '#fff',
  bgHover: dark ? '#303134' : '#ebebeb',
  text: dark ? '#e8eaed' : '#565656',
  textActive: dark ? '#fff' : '#000',
  disabled: dark ? '#6f7275' : '#b0b0b0',
});

const controlFont = 'Roboto, Arial, sans-serif';

/**
 * Google-style "Places" map control: chooses which Google points of
 * interest (hotels, shops, …) the base map shows. Rendered inside a
 * GoogleMap; placed in the top-left control area next to the map type
 * control.
 *
 * @param {object} props
 * @param {object} props.preset current MAP_PLACES_PRESETS entry
 * @param {(id:string) => void} props.onChange
 * @param {boolean} [props.dark]
 */
export default function MapPlacesControl({ preset, onChange, dark = false }) {
  const map = useGoogleMap();
  const [container] = useState(() => document.createElement('div'));
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const controls = map.controls[window.google.maps.ControlPosition.TOP_LEFT];
    controls.push(container);
    return () => {
      const i = controls.getArray().indexOf(container);
      if (i >= 0) controls.removeAt(i);
    };
  }, [map, container]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const c = palette(dark);
  const hidden = preset.id !== 'all';
  // Like Google's control: open on hover, stay open briefly after leaving.
  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 250); };

  return createPortal(
    <div
      style={{ position: 'relative', margin: '10px 0 0 10px', fontFamily: controlFont }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Show or hide Google places (hotels, shops, restaurants…)"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 40,
          padding: '0 17px',
          border: 0,
          borderRadius: 2,
          background: open ? c.bgHover : c.bg,
          color: hidden ? c.textActive : c.text,
          fontFamily: controlFont,
          fontSize: 18,
          fontWeight: hidden ? 500 : 400,
          cursor: 'pointer',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
          whiteSpace: 'nowrap',
        }}
      >
        Places{hidden ? `: ${preset.id === 'none' ? 'hidden' : 'no businesses'}` : ''}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 40,
            left: 0,
            minWidth: '100%',
            padding: '4px 0',
            background: c.bg,
            borderRadius: '0 0 2px 2px',
            boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
            zIndex: 1,
          }}
        >
          {MAP_PLACES_PRESETS.map((p) => {
            const available = isPresetAvailable(p);
            const checked = p.id === preset.id;
            return (
              <label
                key={p.id}
                title={available ? p.help : `${p.help}. Not set up yet: needs a Google Cloud map ID with this style.`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  fontSize: 16,
                  whiteSpace: 'nowrap',
                  color: available ? (checked ? c.textActive : c.text) : c.disabled,
                  cursor: available ? 'pointer' : 'not-allowed',
                }}
              >
                <input
                  type="radio"
                  name="map-places-preset"
                  checked={checked}
                  disabled={!available}
                  onChange={() => {
                    setOpen(false);
                    onChange(p.id);
                  }}
                  style={{ margin: 0, cursor: 'inherit' }}
                />
                {p.label}
              </label>
            );
          })}
        </div>
      )}
    </div>,
    container,
  );
}
